import { useRef, useState } from "react";
import { Plus, Trash2, MoreVertical, PencilLine, Share2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import type { ConversationSummary } from "@/stores/chat/chatStore";
import { AmyAvatar } from "./AmyAvatar";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

interface ChatSidebarProps {
  conversations: ConversationSummary[];
  sharedConversations: ConversationSummary[];
  activeId: string | null;
  onOpen: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onShare: (id: string, shared: boolean) => void;
  onRename: (id: string, title: string) => void;
  onNew: () => void;
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="px-4 pb-1 pt-3 text-xs font-medium text-sidebar-foreground/70">{children}</p>
);

/**
 * Recent-conversations rail with Amy's identity, new-chat, per-row share/delete,
 * and a "Shared with you" section (chats teammates shared with the org).
 */
export function ChatSidebar({
  conversations,
  sharedConversations,
  activeId,
  onOpen,
  onDelete,
  onShare,
  onRename,
  onNew,
}: ChatSidebarProps) {
  // Inline rename: which row is being edited + the draft text.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const renameInput = useRef<HTMLInputElement>(null);

  /** A single conversation row. `readOnly` rows (shared by others) open-only, no menu. */
  const renderRow = (c: ConversationSummary, readOnly: boolean) => {
    const active = activeId === c.id;
    const editing = editingId === c.id;
    const commitRename = () => {
      setEditingId(null);
      const next = draft.trim();
      if (next && next !== c.title) onRename(c.id, next);
    };
    return (
      <ScopeWrapper key={c.id}>
        <div
          data-context-menu
          onClick={() => onOpen(c.id)}
          className={cn(
            "group relative flex h-8.5 w-64 cursor-pointer items-center gap-2 rounded-lg pl-3 pr-1 text-sm transition-none",
            active ? "bg-muted font-medium" : "hover:bg-sidebar"
          )}
        >
          {editing ? (
            <input
              ref={renameInput}
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") setEditingId(null);
              }}
              onBlur={commitRename}
              maxLength={60}
              aria-label="Rename conversation"
              className="min-w-0 flex-1 rounded-sm bg-transparent text-sm outline-none ring-1 ring-primary/40 px-1 -mx-1 mr-1"
            />
          ) : c.titlePending ? (
            <Skeleton className="h-3.5 flex-1 w-full rounded-xs" />
          ) : (
            <span className="flex-1 truncate">{c.title || "Untitled"}</span>
          )}

          {readOnly ? (
            <Users className="mr-1.5 size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <>
              {c.shared && (
                <Users className="mr-1 size-3.5 shrink-0 text-muted-foreground group-hover:hidden" />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    onClick={(e) => e.stopPropagation()}
                    className="size-7 shrink-0 rounded-sm hidden group-hover:flex data-[state=open]:flex"
                    aria-label="Conversation options"
                    variant="muted-ghost"
                    size="icon-sm"
                    scope={false}
                  >
                    <MoreVertical className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  // The menu holds focus until it has closed, then returns it to the
                  // ⋯ button: after Rename, hand it to the input instead.
                  onCloseAutoFocus={(e) => {
                    if (!renameInput.current) return;
                    e.preventDefault();
                    renameInput.current.focus();
                  }}
                >
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setDraft(c.title || "");
                      setEditingId(c.id);
                    }}
                  >
                    <PencilLine />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onShare(c.id, !c.shared);
                    }}
                  >
                    <Share2 />
                    {c.shared ? "Unshare" : "Share with Org."}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => onDelete(e, c.id)} variant="destructive">
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </ScopeWrapper>
    );
  };

  return (
    <aside className="hidden shrink-0 flex-col border-r md:flex overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent] scrollbar-thin">
      {/* identity */}
      <div className="flex items-center gap-2.5 px-3.5 pb-3 pt-3.5">
        <AmyAvatar className="size-7.5 border-0 rounded-none" />
        <div className="leading-tight">
          <p className="text-sm font-medium">Amy</p>
          <p className="text-[0.7rem] text-muted-foreground">Data assistant</p>
        </div>
        <Button onClick={onNew} variant="flat" className="ml-auto" size="sm">
          <Plus />
          New
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="pb-4">
          {sharedConversations.length > 0 && (
            <>
              <SectionLabel>Shared with you</SectionLabel>
              <div className="flex flex-col gap-1 px-2.5 pt-1.5">
                {sharedConversations.map((c) => renderRow(c, true))}
              </div>
            </>
          )}

          <SectionLabel>Recent</SectionLabel>
          <div className="flex flex-col gap-1 px-2.5 pt-1.5">
            {conversations.map((c) => renderRow(c, false))}
            {conversations.length === 0 && (
              <p className="px-3 py-3 text-xs text-muted-foreground w-64">No conversations yet.</p>
            )}
          </div>
        </div>
      </ScrollArea>
    </aside>
  );
}
